import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import * as menuActions from "../../../store/actions/menuActions";
import { Box, Button, Divider, Modal, ModalDialog, Typography } from "@mui/joy";
import { Delete, WarningRounded } from "@mui/icons-material";
import { Order } from "../../Menu/Menu";
import Select from "../../UI/Select";
import { SelectChangeEvent } from "@mui/material";
import { formatCurrency } from "../../../helpers/utils";

interface OrderModalProps {
    open: boolean;
    orders: Order[];
    onHandleClose: () => void;
    handleGenerateReport: () => Promise<void>;
}

const DeleteOrderModal: FC<OrderModalProps> = ({ open, orders, onHandleClose, handleGenerateReport }) => {
    const [selectedOrderId, setSelectedOrderId] = useState<string>("");

    useEffect(() => {
        if (orders.length === 1) {
            setSelectedOrderId(orders[0].id);
        }
    }, [orders]);

    const dispatch = useDispatch();
    const { deleteOrder } = bindActionCreators(menuActions, dispatch);

    const handleSelectChange = (e: SelectChangeEvent): void => {
        setSelectedOrderId(e.target.value);
    };

    const handleDeleteOrder = (e: React.FormEvent): void => {
        e.preventDefault();
        deleteOrder(selectedOrderId);
        handleGenerateReport();
        handleCloseModal();
    };

    const handleCloseModal = () => {
        setSelectedOrderId("");
        onHandleClose();
    };

    const selectedOrder = orders.find(order => order.id === selectedOrderId);

    return (
        <Modal open={open} onClose={handleCloseModal}>
            <ModalDialog
                variant="outlined"
                role="alertdialog"
                aria-labelledby="alert-dialog-modal-title"
                aria-describedby="alert-dialog-modal-description"
            >
                <Typography id="alert-dialog-modal-title" level="h2" startDecorator={<WarningRounded />}>
                    Brisanje porudžbine
                </Typography>
                <Divider />
                <Typography id="alert-dialog-modal-description" textColor="text.tertiary">
                    Da li ste sigurni da želite da obrišete porudžbinu?
                </Typography>

                <Divider />
                {orders.length > 1 && (
                    <Box>
                        <Typography level="body-sm" sx={{ mb: 0.5 }}> Odaberite porudžbinu za brisanje:</Typography>
                        <Select
                            label="Porudzbine"
                            value={selectedOrderId}
                            options={orders.map(order => ({ key: order.id, value: order.id }))}
                            onSelectChange={handleSelectChange}
                        />
                    </Box>
                )}
                {selectedOrder && (
                    <>
                        <Divider />
                        <Typography level="body-sm">Obroci u porudžbini:</Typography>
                        <Box
                            sx={{
                                backgroundColor: "background.level1",
                                borderRadius: "sm",
                                p: 1
                            }}
                        >
                            {selectedOrder.meals.map((meal, i) => (
                                <Box
                                    key={meal.portionId}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        borderBottom: i !== selectedOrder.meals.length - 1 ? "1px solid" : "none",
                                        borderColor: "divider",
                                        py: 0.5
                                    }}
                                >
                                    <Typography level="body-sm" fontWeight="md">
                                        {meal.name} ({meal.size})
                                    </Typography>
                                    <Typography level="body-sm" textColor="text.tertiary">
                                        x {meal.amount} — {formatCurrency(meal.price)} RSD
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", pt: 2 }}>
                    <Button
                        variant="plain"
                        color="neutral"
                        onClick={handleCloseModal}
                    >
                        Otkaži
                    </Button>
                    <Button
                        variant="solid"
                        color="danger"
                        disabled={orders.length === 0 || (orders.length > 1 && !selectedOrderId)}
                        startDecorator={<Delete />}
                        onClick={handleDeleteOrder}
                    >
                        Obriši porudžbinu
                    </Button>
                </Box>
            </ModalDialog>
        </Modal >
    );
}

export default DeleteOrderModal;